<template>
  <div class="grid lg:grid-cols-3 md:grid-cols-1 sm:grid-cols-1 gap-4">
    <CardComponent>
      <div>
        <h6 class="font-medium text-sm text-grey-light-600">Active payment plans value</h6>
        <h2 class="font-semibold text-4xl mt-2">
          <Skeleton v-if="isLoading" class="h-10 w-full" />
          <template v-else>
            {{ activePaymentPlansValue ? displayCurrency(activePaymentPlansValue) : displayCurrency(0) }}
          </template>
        </h2>
      </div>
    </CardComponent>
    <CardComponent>
      <div>
        <h6 class="font-medium text-sm text-grey-light-600">Due payments</h6>
        <h2 class="font-semibold text-4xl mt-2">
          <Skeleton v-if="isLoading" class="h-10 w-full" />
          <template v-else>
            {{ duePayments ? displayCurrency(duePayments) : displayCurrency(0) }}
          </template>
        </h2>
      </div>
    </CardComponent>
    <CardComponent>
      <div>
        <h6 class="font-medium text-sm text-grey-light-600">Average payment plan value</h6>
        <h2 class="font-semibold text-4xl mt-2">
          <Skeleton v-if="isLoading" class="h-10 w-full" />
          <template v-else>
            {{ averagePaymentPlansValue ? displayCurrency(averagePaymentPlansValue) : displayCurrency(0) }}
          </template>
        </h2>
      </div>
    </CardComponent>
  </div>
</template>

<script setup lang="ts">
import { Skeleton } from '@/components/ui/skeleton';
import { NetworkError } from '@/errors';
import { getAggregatedGraphData } from '@/services/graph';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { storeToRefs } from 'pinia';
import { onMounted, ref, watch } from 'vue';
import CardComponent from '../../CardComponent.vue';

const userStore = useUserStore();
const merchantStore = useMerchantStore();
const { user } = storeToRefs(userStore);

const isLoading = ref(true);
const duePayments = ref(0);
const activePaymentPlansValue = ref(0);
const averagePaymentPlansValue = ref(0);

onMounted(async () => await getGraphData());

watch(
  () => merchantStore.merchant,
  async (newMerchant) => {
    if (newMerchant) {
      await getGraphData();
    }
  },
);

const checkMerchantId = () => {
  const merchantId = user.value._id;
  if (merchantId == null || merchantId === '' || merchantId === 'undefined') return false;
  return true;
};

const getGraphData = async () => {
  if (checkMerchantId()) {
    isLoading.value = true;

    try {
      const response = await getAggregatedGraphData();

      if (response == null) return;

      if (response.planValue.graph.length > 0) {
        activePaymentPlansValue.value = response.planValue.graph[0].paymentPlanValue;
      }

      if (response.paymentsDue.graph.length > 0) {
        duePayments.value = response.paymentsDue.graph[0].sum;
      }

      if (response.planValueAverage.graph.length > 0) {
        averagePaymentPlansValue.value = response.planValueAverage.graph[0].averagePaymentPlanValue;
      }
    } catch (error) {
      if (error instanceof NetworkError) {
        createWarning(error);
        addMetric('merchant-info-cards', { status: 'network_error', channel: 'merchant' });
      } else {
        createException(error);
        addMetric('merchant-info-cards', { status: 'error', channel: 'merchant' });
      }
    } finally {
      isLoading.value = false;
    }
  }
};
</script>
